import { Box, Button, ChevronLeftIcon, FormControl, HStack, Input, Link, Stack, Text, VStack } from 'native-base';
import React, { memo, useContext, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import has from 'lodash.has';
import { FormatWithMask, MaskedInput, mobileMask, zipCodeMask } from '../../MaskedInput';
import { yupResolver } from '@hookform/resolvers/yup';
import { DropDown } from '../../DropDown';
import { ContactInformationSchema, States } from '../../../utils';
import { NavigationContext } from '../../../contexts';
import { produce } from 'immer';
import Functions from '../../../utils/Functions';
const ContactInformation = _ref => {
  var _errors$firstName, _errors$lastName, _errors$email, _errors$phoneNumber, _errors$street, _errors$city, _errors$zipCode;
  let {
    navigation: {
      current,
      back,
      next
    }
  } = _ref;
  const {
    onSelected,
    setStep,
    review,
    step,
    type,
    logUserEvent
  } = useContext(NavigationContext);
  let {
    firstName,
    lastName,
    email,
    phoneNumber,
    address: {
      street,
      street2,
      city,
      state,
      zipCode
    }
  } = useMemo(() => review[step], [review, step]);
  phoneNumber = FormatWithMask({
    mask: mobileMask,
    text: phoneNumber
  }).unmasked;
  const {
    control,
    handleSubmit,
    formState: {
      errors
    },
    trigger
  } = useForm({
    resolver: yupResolver(ContactInformationSchema),
    mode: 'onChange',
    defaultValues: {
      firstName,
      lastName,
      email,
      phoneNumber,
      street,
      street2,
      city,
      state,
      zipCode
    }
  });
  useEffect(() => {
    trigger().catch(console.log);
  }, [trigger]);
  const onSubmit = data => {
    logUserEvent({
      type,
      action: `${type}FormSubmitClicked`
    });
    const formattedData = produce(review[step], draft => {
      draft.fullName = `${data.firstName} ${data.lastName}`;
      draft.formattedPhoneNumber = `${FormatWithMask({
        mask: mobileMask,
        text: data.phoneNumber
      }).masked}`;
      draft.firstName = data.firstName;
      draft.lastName = data.lastName;
      draft.email = data.email;
      draft.phoneNumber = data.phoneNumber;
      draft.propertyInformation = Functions.formatPropertyAddress({
        street: data.street,
        street2: data.street2,
        city: data.city,
        state: data.state,
        zipCode: data.zipCode
      });
      draft.address.street = data.street;
      draft.address.street2 = data.street2;
      draft.address.city = data.city;
      draft.address.state = data.state;
      draft.address.zipCode = data.zipCode;
    });
    onSelected({
      value: formattedData,
      navigation: {
        action: 'next',
        next,
        back,
        current
      }
    });
  };
  return /*#__PURE__*/React.createElement(VStack, null, /*#__PURE__*/React.createElement(VStack, {
    space: {
      base: 7,
      md: 2,
      lg: 2
    },
    mb: {
      base: 8,
      md: 4,
      lg: 4
    }
  }, /*#__PURE__*/React.createElement(Text, {
    fontSize: "40",
    fontWeight: "light",
    color: "text.running",
    lineHeight: "sm",
    accessibilityLabel: "Review and confirm your contact information"
  }, "Review and confirm your contact information"), /*#__PURE__*/React.createElement(Text, {
    fontSize: "sm",
    fontWeight: "medium",
    color: "text.running",
    accessibilityLabel: "required fields are marked with asterisks"
  }, "Required fields are marked with *")), /*#__PURE__*/React.createElement(Stack, {
    direction: {
      sm: 'column',
      md: 'row',
      lg: 'row'
    },
    flexWrap: "wrap",
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    maxWidth: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    isInvalid: has(errors, 'firstName'),
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "first name"
  }, "FIRST NAME *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref2 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref2;
      return /*#__PURE__*/React.createElement(Input, {
        accessibilityLabel: "first name text field",
        height: 12.5,
        onBlur: onBlur,
        placeholder: "First Name",
        onChangeText: val => onChange(val),
        value: value,
        color: "text.running",
        placeholderTextColor: "grey.900",
        variant: "unstyled"
      });
    },
    name: "firstName",
    defaultValue: firstName
  }), /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    accessibilityLabel: errors.firstName ? 'please enter valid first name' : ''
  }, (_errors$firstName = errors.firstName) === null || _errors$firstName === void 0 ? void 0 : _errors$firstName.message)), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'lastName'),
    maxW: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "last name"
  }, "LAST NAME *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref3 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref3;
      return /*#__PURE__*/React.createElement(Input, {
        accessibilityLabel: "last name text field",
        height: 12.5,
        onBlur: onBlur,
        placeholder: "Last Name",
        onChangeText: val => onChange(val),
        value: value,
        color: "text.running",
        placeholderTextColor: "grey.900",
        variant: "unstyled"
      });
    },
    name: "lastName",
    defaultValue: lastName
  }), /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    accessibilityLabel: errors.lastName ? 'please enter valid last name' : ''
  }, (_errors$lastName = errors.lastName) === null || _errors$lastName === void 0 ? void 0 : _errors$lastName.message)), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'email'),
    maxW: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "email address"
  }, "EMAIL *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref4 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref4;
      return /*#__PURE__*/React.createElement(Input, {
        accessibilityLabel: "please enter email address",
        height: 12.5,
        onBlur: onBlur,
        placeholder: "Email Address",
        onChangeText: val => onChange(val),
        value: value,
        color: "text.running",
        placeholderTextColor: "grey.900",
        variant: "unstyled"
      });
    },
    name: "email",
    defaultValue: email
  }), /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    accessibilityLabel: errors.email ? 'please enter valid email address' : ''
  }, (_errors$email = errors.email) === null || _errors$email === void 0 ? void 0 : _errors$email.message)), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'phoneNumber'),
    maxW: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "phone number"
  }, "PHONE NUMBER *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref5 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref5;
      return /*#__PURE__*/React.createElement(MaskedInput, {
        accessibilityLabel: "please enter phone number",
        onBlur: onBlur,
        mask: mobileMask,
        placeholder: "###-###-####",
        onChangeText: _ref6 => {
          let {
            unmasked
          } = _ref6;
          onChange(unmasked);
        },
        value: value,
        variant: "unstyled"
      });
    },
    defaultValue: phoneNumber,
    name: "phoneNumber"
  }), /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    accessibilityLabel: errors.phoneNumber ? 'please enter valid phone number' : ''
  }, (_errors$phoneNumber = errors.phoneNumber) === null || _errors$phoneNumber === void 0 ? void 0 : _errors$phoneNumber.message)), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'street'),
    maxW: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "current street address"
  }, "CURRENT STREET ADDRESS *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref7 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref7;
      return /*#__PURE__*/React.createElement(Input, {
        accessibilityLabel: "please enter current street address",
        height: 12.5,
        onBlur: onBlur,
        placeholder: "Street Address",
        onChangeText: val => onChange(val),
        value: value,
        color: "text.running",
        placeholderTextColor: "grey.900",
        variant: "unstyled"
      });
    },
    name: "street",
    defaultValue: street
  }), /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    accessibilityLabel: errors.street ? 'please enter valid current street address' : ''
  }, (_errors$street = errors.street) === null || _errors$street === void 0 ? void 0 : _errors$street.message)), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'street2'),
    maxW: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "apartment or unit"
  }, "APARTMENT/UNIT"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref8 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref8;
      return /*#__PURE__*/React.createElement(Input, {
        accessibilityLabel: "please enter apartment or unit",
        height: 12.5,
        onBlur: onBlur,
        placeholder: "Apartment/Unit Number",
        onChangeText: val => onChange(val),
        value: value,
        color: "text.running",
        placeholderTextColor: "grey.900",
        variant: "unstyled"
      });
    },
    defaultValue: street2,
    name: "street2"
  })), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'city'),
    maxW: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "current city"
  }, "CURRENT CITY *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref9 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref9;
      return /*#__PURE__*/React.createElement(Input, {
        accessibilityLabel: "please enter current city",
        height: 12.5,
        onBlur: onBlur,
        placeholder: "Current City",
        onChangeText: val => onChange(val),
        value: value,
        color: "text.running",
        placeholderTextColor: "grey.900",
        variant: "unstyled"
      });
    },
    defaultValue: city,
    name: "city"
  }), /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    accessibilityLabel: errors.city ? 'please enter valid current city' : ''
  }, (_errors$city = errors.city) === null || _errors$city === void 0 ? void 0 : _errors$city.message)), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'state'),
    mb: {
      base: 0,
      md: 2,
      lg: 2
    },
    w: {
      sm: '100%',
      md: 315,
      lg: 315
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "current state"
  }, "CURRENT STATE *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref10 => {
      let {
        field: {
          onChange,
          value
        }
      } = _ref10;
      return /*#__PURE__*/React.createElement(DropDown, {
        accessibilityLabel: "please select current state",
        w: {
          sm: '100%',
          md: 315,
          lg: 315
        },
        selectedValue: value,
        onValueChange: onChange,
        list: States
      });
    },
    name: "state",
    defaultValue: state
  })), /*#__PURE__*/React.createElement(FormControl, {
    h: "28",
    isInvalid: has(errors, 'zipCode'),
    maxW: {
      sm: '100%',
      md: 315,
      lg: 315
    },
    mb: {
      base: 0,
      md: 2,
      lg: 2
    }
  }, /*#__PURE__*/React.createElement(FormControl.Label, {
    accessibilityLabel: "current zip"
  }, "CURRENT ZIP *"), /*#__PURE__*/React.createElement(Controller, {
    control: control,
    render: _ref11 => {
      let {
        field: {
          onChange,
          onBlur,
          value
        }
      } = _ref11;
      return /*#__PURE__*/React.createElement(MaskedInput, {
        accessibilityLabel: "please enter zip",
        onBlur: onBlur,
        mask: zipCodeMask,
        placeholder: "#####-####",
        onChangeText: _ref12 => {
          let {
            masked
          } = _ref12;
          onChange(masked);
        },
        value: value,
        variant: "unstyled",
        keyboardType: "number-pad"
      });
    },
    name: "zipCode",
    defaultValue: zipCode
  }), /*#__PURE__*/React.createElement(FormControl.ErrorMessage, {
    accessibilityLabel: errors.zipCode ? 'please enter valid zip' : ''
  }, (_errors$zipCode = errors.zipCode) === null || _errors$zipCode === void 0 ? void 0 : _errors$zipCode.message))), /*#__PURE__*/React.createElement(HStack, {
    justifyContent: "space-between",
    my: "5"
  }, /*#__PURE__*/React.createElement(HStack, {
    justifyContent: "center",
    alignItems: "center",
    space: "1"
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(ChevronLeftIcon, {
    color: "blue.400",
    size: "5"
  })), /*#__PURE__*/React.createElement(Link, {
    onPress: () => {
      logUserEvent({
        type,
        action: `${type}CashOutFormBackClicked`
      });
      setStep(back);
    },
    accessibilityLabel: "back",
    accessibilityRole: "link",
    _text: {
      fontSize: 'lg',
      color: 'blue.400',
      textDecoration: 'none'
    },
    testID: "contactinfo-back-button"
  }, "Back")), /*#__PURE__*/React.createElement(Button, {
    variant: "freedom",
    w: "140",
    _text: {
      fontWeight: 'bold',
      fontSize: 'lg'
    },
    accessibilityLabel: "Update",
    accessibilityRole: "button",
    onPress: handleSubmit(onSubmit),
    isDisabled: !Functions.isEmpty(errors),
    _disabled: {
      bg: 'grey.200'
    },
    testID: "contactinfo-update-button"
  }, "UPDATE")));
};
export default /*#__PURE__*/memo(ContactInformation);
//# sourceMappingURL=index.js.map